/**
 * ReportContentModal - Modal for reporting inappropriate content
 * Required by Apple App Store for apps with user-generated content
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle, Flag } from 'lucide-react';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface ReportContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  contentType: 'post' | 'comment';
  contentId: string;
  contentAuthorId?: string;
  franchiseId?: string;
}

const REPORT_REASONS = [
  'spam',
  'harassment',
  'inappropriate',
  'violence',
  'falseInfo',
  'other'
] as const;

type ReportReason = typeof REPORT_REASONS[number];

export function ReportContentModal({
  isOpen,
  onClose,
  contentType,
  contentId,
  contentAuthorId,
  franchiseId
}: ReportContentModalProps) {
  const { t } = useTranslation();
  const { firebaseUser, user } = useAuth();
  const [selectedReason, setSelectedReason] = useState<ReportReason | null>(null);
  const [additionalDetails, setAdditionalDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!selectedReason) {
      toast.error(t('report.selectReason'));
      return;
    }

    if (!firebaseUser) {
      toast.error(t('report.mustBeLoggedIn'));
      return;
    }

    setSubmitting(true);
    try {
      await addDoc(collection(db, 'content_reports'), {
        contentType,
        contentId,
        contentAuthorId: contentAuthorId || null,
        reportedBy: firebaseUser.uid,
        reporterEmail: firebaseUser.email || null,
        franchiseId: franchiseId || user?.franchiseId || null,
        reason: selectedReason,
        additionalDetails: additionalDetails.trim() || null,
        status: 'pending',
        createdAt: Timestamp.now(),
        reviewedAt: null,
        reviewedBy: null,
        action: null
      });

      toast.success(t('report.submitted'));
      onClose();
      setSelectedReason(null);
      setAdditionalDetails('');
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error(t('report.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 pb-24 lg:pb-4 bg-black/50 overflow-y-auto">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-content-modal-title"
        className="bg-surface rounded-xl w-full max-w-md shadow-xl my-auto max-h-[85vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-hairline flex-shrink-0">
          <div className="flex items-center gap-2">
            <Flag className="w-5 h-5 text-danger" />
            <h2 id="report-content-modal-title" className="text-lg font-semibold">
              {t('report.title')}
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label={t('common.close')}
            className="p-1 hover:bg-surface-2 rounded-full"
          >
            <X className="w-5 h-5 text-ink-muted" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          <p className="text-sm text-ink-muted">
            {t('report.description')}
          </p>

          {/* Reason Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t('report.reasonLabel')}
            </label>
            <div className="space-y-2">
              {REPORT_REASONS.map((reason) => (
                <button
                  key={reason}
                  onClick={() => setSelectedReason(reason)}
                  className={`w-full p-3 text-left rounded-lg border transition-colors ${
                    selectedReason === reason
                      ? 'border-danger/30 bg-danger/10'
                      : 'border-hairline hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <span className={`text-sm ${
                    selectedReason === reason
                      ? 'text-danger font-medium'
                      : 'text-ink-muted'
                  }`}>
                    {t(`report.reasons.${reason}`)}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Additional Details */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t('report.additionalDetails')} <span className="text-ink-subtle">({t('common.optional')})</span>
            </label>
            <textarea
              value={additionalDetails}
              onChange={(e) => setAdditionalDetails(e.target.value)}
              placeholder={t('report.detailsPlaceholder')}
              className="w-full p-3 border rounded-lg resize-none h-24 bg-surface-2 border-hairline"
              maxLength={500}
            />
          </div>

          {/* Warning */}
          <div className="flex items-start gap-2 p-3 bg-accent/10 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
            <p className="text-xs text-accent">
              {t('report.warning')}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-hairline flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-ink-muted bg-surface-2 rounded-lg hover:bg-surface-3 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedReason || submitting}
            className="flex-1 px-4 py-2 text-white bg-danger/100 rounded-lg hover:bg-danger/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? t('common.submitting') : t('report.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}
